import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Layer, Map, Marker, NavigationControl, Popup, Source, type MapLayerMouseEvent, type MapRef } from '@vis.gl/react-maplibre';
import type { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { Component, useEffect, useMemo, useRef, useState, type ErrorInfo, type ReactNode } from 'react';

import { MapOverlayLegend, MapOverlayPopup } from './CanvassingSessionMapOverlayRecords';
import {
  clusterClickAction,
  isDegenerateFitBounds,
  MAP_OVERLAY_CLUSTER_MAX_ZOOM,
  MAP_OVERLAY_CLUSTER_RADIUS,
  toOverlayFeatureCollection,
  unionFitBounds,
  VISIT_CLUSTER_LAYER_ID,
  VISIT_COUNT_LAYER_ID,
  VISIT_ICON_ID,
  VISIT_POINT_LAYER_ID,
  VISIT_SOURCE_ID,
  VOTER_CLUSTER_LAYER_ID,
  VOTER_COUNT_LAYER_ID,
  VOTER_POINT_LAYER_ID,
  VOTER_SOURCE_ID,
  type MapOverlayBounds,
  type MapOverlayVisit,
  type MapOverlayVoter,
  type MapPopupRecord,
} from './canvassingSessionMapOverlay';
import type { CanvassingSessionGpsTracePoint } from './useCanvassingSessionGpsTrace';

export const OPEN_FREE_MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
export const MAPLIBRE_WORKER_URL = '/assets/maplibre-gl-worker.mjs';
export const GPS_TRACE_MAP_HEIGHT = { xs: 320, md: 440 } as const;

export type CanvassingSessionGpsTraceMapProps = {
  sessionId: string;
  points: readonly CanvassingSessionGpsTracePoint[];
  displayedCount: number;
  totalCount: number;
  fitRevision: number;
  onMapError: () => void;
  overlayVoters?: readonly MapOverlayVoter[];
  overlayVisits?: readonly MapOverlayVisit[];
  renderVoters?: boolean;
  renderVisits?: boolean;
  serverBounds?: MapOverlayBounds | null;
  cameraFitRevision?: number;
  showMapRecordsLegend?: boolean;
  onMapReady?: (map: ReturnType<MapRef['getMap']>) => void;
};

type TraceMapErrorBoundaryProps = {
  onMapError: () => void;
  children: ReactNode;
};

type TraceMapErrorBoundaryState = { failed: boolean };

class TraceMapErrorBoundary extends Component<TraceMapErrorBoundaryProps, TraceMapErrorBoundaryState> {
  state: TraceMapErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): TraceMapErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    this.props.onMapError();
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

const endpointShape = (kind: 'start' | 'end' | 'combined') => {
  if (kind === 'start') {
    return {
      width: 14,
      height: 14,
      border: '2px solid',
      borderColor: 'common.white',
      bgcolor: 'primary.main',
      borderRadius: 1,
      boxShadow: 2,
      transform: 'rotate(45deg)',
    };
  }

  if (kind === 'end') {
    return {
      width: 16,
      height: 16,
      border: '3px solid',
      borderColor: 'common.white',
      bgcolor: 'primary.main',
      borderRadius: '50%',
      boxShadow: 2,
    };
  }

  return {
    width: 18,
    height: 18,
    border: '3px solid',
    borderColor: 'common.white',
    bgcolor: 'primary.main',
    borderRadius: '50%',
    boxShadow: 2,
    '&::after': {
      content: '""',
      display: 'block',
      width: 6,
      height: 6,
      m: '3px',
      bgcolor: 'common.white',
      borderRadius: 0.5,
      transform: 'rotate(45deg)',
    },
  };
};

const TraceLegend = ({ pointCount }: { pointCount: number }) => {
  if (pointCount === 0) return null;
  if (pointCount === 1) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} aria-label="GPS trace endpoint legend">
        <Box aria-hidden sx={endpointShape('combined')} />
        <Typography variant="body2">Start and end: the only displayed valid GPS point</Typography>
      </Stack>
    );
  }

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} aria-label="GPS trace endpoint legend">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box aria-hidden sx={endpointShape('start')} />
        <Typography variant="body2">Start: first displayed valid GPS point</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box aria-hidden sx={endpointShape('end')} />
        <Typography variant="body2">End: last displayed valid GPS point</Typography>
      </Stack>
    </Stack>
  );
};

type ClusterLeaf = Feature<Geometry | null, GeoJsonProperties>;

type ClusterSource = {
  getClusterExpansionZoom: (clusterId: number) => Promise<number>;
  getClusterLeaves: (clusterId: number, limit: number, offset: number) => Promise<ClusterLeaf[]>;
};

const isClusterSource = (value: unknown): value is ClusterSource => {
  if (typeof value !== 'object' || value === null) return false;
  return typeof Reflect.get(value, 'getClusterExpansionZoom') === 'function'
    && typeof Reflect.get(value, 'getClusterLeaves') === 'function';
};

const pointCoordinates = (feature: ClusterLeaf): [number, number] | null => {
  if (feature.geometry === null || feature.geometry.type !== 'Point') return null;
  const [lng, lat] = feature.geometry.coordinates;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;
  return [lng, lat];
};

const ensureVisitIcon = (map: { hasImage: (id: string) => boolean; addImage: (id: string, image: ImageData, options?: { pixelRatio?: number }) => void }, fill: string, stroke: string): void => {
  if (map.hasImage(VISIT_ICON_ID)) return;
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context === null) return;
  context.fillStyle = fill;
  context.strokeStyle = stroke;
  context.lineWidth = 4;
  context.fillRect(4, 4, 24, 24);
  context.strokeRect(4, 4, 24, 24);
  map.addImage(VISIT_ICON_ID, context.getImageData(0, 0, size, size), { pixelRatio: 2 });
};

const TraceMap = ({
  sessionId,
  points,
  displayedCount,
  totalCount,
  fitRevision,
  onMapError,
  overlayVoters = [],
  overlayVisits = [],
  renderVoters = false,
  renderVisits = false,
  serverBounds = null,
  cameraFitRevision,
  showMapRecordsLegend = false,
  onMapReady,
}: CanvassingSessionGpsTraceMapProps) => {
  const theme = useTheme();
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const fittedRevision = useRef<number | null>(null);
  const fittedCameraRevision = useRef<number | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [popup, setPopup] = useState<{ records: MapPopupRecord[]; longitude: number; latitude: number } | null>(null);
  const initialPoint = points[0];
  const finalPoint = points.at(-1);
  const anchor = initialPoint ?? overlayVisits[0] ?? overlayVoters[0];
  const anchorLongitude = anchor?.lng ?? (serverBounds === null ? 0 : (serverBounds.west + serverBounds.east) / 2);
  const anchorLatitude = anchor?.lat ?? (serverBounds === null ? 0 : (serverBounds.south + serverBounds.north) / 2);

  const lineGeoJson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: points.length >= 2
      ? [{
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: points.map(point => [point.lng, point.lat] as [number, number]),
          },
        }]
      : [],
  }), [points]);

  const voterGeoJson = useMemo(
    () => toOverlayFeatureCollection(overlayVoters.map(voter => ({ id: voter.id, lat: voter.lat, lng: voter.lng })), 'voter'),
    [overlayVoters],
  );
  const visitGeoJson = useMemo(
    () => toOverlayFeatureCollection(overlayVisits.map(visit => ({ id: String(visit.doorVisitId), lat: visit.lat, lng: visit.lng })), 'visit'),
    [overlayVisits],
  );

  const bounds = useMemo<[[number, number], [number, number]] | null>(() => {
    if (points.length < 2) return null;
    let minLng = points[0]?.lng ?? 0;
    let maxLng = minLng;
    let minLat = points[0]?.lat ?? 0;
    let maxLat = minLat;
    for (const point of points) {
      minLng = Math.min(minLng, point.lng);
      maxLng = Math.max(maxLng, point.lng);
      minLat = Math.min(minLat, point.lat);
      maxLat = Math.max(maxLat, point.lat);
    }
    return [[minLng, minLat], [maxLng, maxLat]];
  }, [points]);

  const closePopup = (): void => {
    setPopup(null);
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    target?.focus();
  };

  useEffect(() => {
    if (popup === null) return;
    const stillVisible = popup.records.some(record => {
      if (record.kind === 'voter') return renderVoters && overlayVoters.some(voter => voter.id === record.voter.id);
      return renderVisits && overlayVisits.some(visit => visit.doorVisitId === record.visit.doorVisitId);
    });
    if (!stillVisible) closePopup();
  }, [overlayVisits, overlayVoters, popup, renderVisits, renderVoters]);

  useEffect(() => {
    if (cameraFitRevision !== undefined || !mapLoaded || bounds === null || fittedRevision.current === fitRevision) return;
    fittedRevision.current = fitRevision;
    try {
      mapRef.current?.fitBounds(bounds, { padding: 48, maxZoom: 17, duration: 0 });
    } catch {
      onMapError();
    }
  }, [bounds, cameraFitRevision, fitRevision, mapLoaded, onMapError]);

  useEffect(() => {
    if (cameraFitRevision === undefined || !mapLoaded || cameraFitRevision === 0 || fittedCameraRevision.current === cameraFitRevision) return;
    const target = unionFitBounds(serverBounds, [
      ...(renderVisits ? overlayVisits : []),
      ...(renderVoters ? overlayVoters : []),
    ]);
    if (target === null) return;
    fittedCameraRevision.current = cameraFitRevision;
    try {
      if (isDegenerateFitBounds(target)) {
        mapRef.current?.jumpTo({ center: [target.east, target.north], zoom: 16 });
      } else {
        mapRef.current?.fitBounds([[target.west, target.south], [target.east, target.north]], { padding: 48, maxZoom: 17, duration: 0 });
      }
    } catch {
      onMapError();
    }
  }, [cameraFitRevision, mapLoaded, onMapError, overlayVisits, overlayVoters, renderVisits, renderVoters, serverBounds]);

  const openPopup = (records: readonly MapPopupRecord[], longitude: number, latitude: number): void => {
    if (records.length === 0) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : containerRef.current;
    setPopup({ records: [...records], longitude, latitude });
  };

  const onMapClick = (event: MapLayerMouseEvent): void => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const layers = [
      renderVoters ? VOTER_CLUSTER_LAYER_ID : null,
      renderVoters ? VOTER_POINT_LAYER_ID : null,
      renderVisits ? VISIT_CLUSTER_LAYER_ID : null,
      renderVisits ? VISIT_POINT_LAYER_ID : null,
    ].filter((layerId): layerId is string => layerId !== null);
    if (layers.length === 0) return;
    const features = map.queryRenderedFeatures(event.point, { layers });
    const clusters = features.filter(feature => feature.properties?.cluster === true);
    const unclustered = features.filter(feature => feature.properties?.cluster !== true);
    void (async () => {
      for (const cluster of clusters) {
        const sourceId = cluster.source;
        const clusterId = cluster.properties?.cluster_id;
        const source = typeof sourceId === 'string' ? map.getSource(sourceId) : undefined;
        if (!isClusterSource(source) || typeof clusterId !== 'number') continue;
        const expansionZoom = await source.getClusterExpansionZoom(clusterId);
        if (clusterClickAction(map.getZoom(), expansionZoom) === 'zoom') {
          const coordinates = pointCoordinates(cluster);
          if (coordinates === null) return;
          map.easeTo({ center: coordinates, zoom: expansionZoom });
          return;
        }
      }

      const voterIds = new Set<string>();
      const visitIds = new Set<string>();
      const remember = (feature: ClusterLeaf): void => {
        const recordId = feature.properties?.recordId;
        const overlay = feature.properties?.overlay;
        if (typeof recordId !== 'string') return;
        if (overlay === 'voter') voterIds.add(recordId);
        if (overlay === 'visit') visitIds.add(recordId);
      };
      unclustered.forEach(remember);
      for (const cluster of clusters) {
        const sourceId = cluster.source;
        const clusterId = cluster.properties?.cluster_id;
        const pointCount = cluster.properties?.point_count;
        const source = typeof sourceId === 'string' ? map.getSource(sourceId) : undefined;
        if (!isClusterSource(source) || typeof clusterId !== 'number' || typeof pointCount !== 'number') continue;
        const leaves = await source.getClusterLeaves(clusterId, pointCount, 0);
        leaves.forEach(remember);
      }
      const records = [
        ...(renderVoters ? overlayVoters.filter(voter => voterIds.has(voter.id)).map(voter => ({ kind: 'voter' as const, voter })) : []),
        ...(renderVisits ? overlayVisits.filter(visit => visitIds.has(String(visit.doorVisitId))).map(visit => ({ kind: 'visit' as const, visit })) : []),
      ];
      openPopup(records, event.lngLat.lng, event.lngLat.lat);
    })().catch(() => onMapError());
  };

  if (anchor === undefined && serverBounds === null) return null;

  return (
    <Box
      ref={containerRef}
      component="section"
      role="region"
      tabIndex={-1}
      aria-label={`GPS trace map for session ${sessionId}: ${displayedCount} displayed of ${totalCount} GPS points`}
    >
      <Box
        data-testid="gps-trace-map-container"
        sx={{ height: GPS_TRACE_MAP_HEIGHT, overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <Map
          ref={mapRef}
          mapStyle={OPEN_FREE_MAP_STYLE}
          workerUrl={MAPLIBRE_WORKER_URL}
          initialViewState={{ longitude: anchorLongitude, latitude: anchorLatitude, zoom: points.length === 1 ? 16 : 13 }}
          attributionControl={{ compact: false }}
          onLoad={() => {
            const map = mapRef.current?.getMap();
            if (map && typeof map.hasImage === 'function' && typeof map.addImage === 'function') {
              ensureVisitIcon(map, theme.palette.warning.main, theme.palette.common.white);
            }
            if (map) onMapReady?.(map);
            setMapLoaded(true);
          }}
          onError={onMapError}
          onClick={onMapClick}
          style={{ width: '100%', height: '100%' }}
        >
          <NavigationControl position="top-right" />
          {points.length >= 2 && (
            <Source id="canvassing-session-gps-trace" type="geojson" data={lineGeoJson}>
              <Layer
                id="canvassing-session-gps-trace-line"
                type="line"
                paint={{ 'line-color': theme.palette.primary.main, 'line-width': 4, 'line-opacity': 0.9 }}
              />
            </Source>
          )}
          {renderVoters && (
            <Source
              id={VOTER_SOURCE_ID}
              type="geojson"
              data={voterGeoJson}
              cluster
              clusterRadius={MAP_OVERLAY_CLUSTER_RADIUS}
              clusterMaxZoom={MAP_OVERLAY_CLUSTER_MAX_ZOOM}
            >
              <Layer
                id={VOTER_CLUSTER_LAYER_ID}
                type="circle"
                filter={['has', 'point_count']}
                paint={{
                  'circle-color': theme.palette.success.main,
                  'circle-radius': 16,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': theme.palette.common.white,
                }}
              />
              <Layer
                id={VOTER_COUNT_LAYER_ID}
                type="symbol"
                filter={['has', 'point_count']}
                layout={{ 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 12 }}
                paint={{ 'text-color': theme.palette.common.white }}
              />
              <Layer
                id={VOTER_POINT_LAYER_ID}
                type="circle"
                filter={['!', ['has', 'point_count']]}
                paint={{
                  'circle-color': theme.palette.success.main,
                  'circle-radius': 5,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': theme.palette.common.white,
                }}
              />
            </Source>
          )}
          {renderVisits && (
            <Source
              id={VISIT_SOURCE_ID}
              type="geojson"
              data={visitGeoJson}
              cluster
              clusterRadius={MAP_OVERLAY_CLUSTER_RADIUS}
              clusterMaxZoom={MAP_OVERLAY_CLUSTER_MAX_ZOOM}
            >
              <Layer
                id={VISIT_CLUSTER_LAYER_ID}
                type="circle"
                filter={['has', 'point_count']}
                paint={{
                  'circle-color': theme.palette.warning.main,
                  'circle-radius': 18,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': theme.palette.common.white,
                }}
              />
              <Layer
                id={VISIT_COUNT_LAYER_ID}
                type="symbol"
                filter={['has', 'point_count']}
                layout={{
                  'text-field': ['concat', ['get', 'point_count_abbreviated'], ' door visits'],
                  'text-size': 11,
                  'text-offset': [0, 1.4],
                }}
                paint={{ 'text-color': theme.palette.text.primary }}
              />
              <Layer
                id={VISIT_POINT_LAYER_ID}
                type="symbol"
                filter={['!', ['has', 'point_count']]}
                layout={{ 'icon-image': VISIT_ICON_ID, 'icon-allow-overlap': true, 'icon-size': 0.8 }}
              />
            </Source>
          )}
          {points.length === 1 && initialPoint !== undefined ? (
            <Marker longitude={initialPoint.lng} latitude={initialPoint.lat} anchor="center">
              <Box aria-hidden sx={endpointShape('combined')} />
            </Marker>
          ) : null}
          {points.length > 1 && initialPoint !== undefined && finalPoint !== undefined ? (
            <>
              <Marker longitude={initialPoint.lng} latitude={initialPoint.lat} anchor="center">
                <Box aria-hidden sx={endpointShape('start')} />
              </Marker>
              <Marker longitude={finalPoint.lng} latitude={finalPoint.lat} anchor="center">
                <Box aria-hidden sx={endpointShape('end')} />
              </Marker>
            </>
          ) : null}
          {popup !== null && (
            <Popup longitude={popup.longitude} latitude={popup.latitude} closeButton={false} closeOnClick={false} focusAfterOpen={false} onClose={closePopup}>
              <MapOverlayPopup records={popup.records} onClose={closePopup} />
            </Popup>
          )}
        </Map>
      </Box>
      <Stack spacing={1} sx={{ mt: 1 }}>
        <TraceLegend pointCount={points.length} />
        <MapOverlayLegend showVoters={renderVoters} showVisits={renderVisits} showMapRecordsLegend={showMapRecordsLegend} />
      </Stack>
    </Box>
  );
};

export const CanvassingSessionGpsTraceMap = (props: CanvassingSessionGpsTraceMapProps) => {
  const hasOverlay = (props.overlayVoters?.length ?? 0) > 0
    || (props.overlayVisits?.length ?? 0) > 0
    || props.serverBounds != null;
  if (props.points.length === 0 && !hasOverlay) return null;

  return (
    <TraceMapErrorBoundary onMapError={props.onMapError}>
      <TraceMap {...props} />
    </TraceMapErrorBoundary>
  );
};
